import { HarvestProject, HarvestUser } from "resource-planner-model";
import env from "../app/Environment";
import fetchService from "../utils/FetchService";
import { HarvestBudget, HarvestTeamTimeReport } from "./HarvestTypes";

interface ProjectBudgetEntry {
  harvestProjectId: string;
  consumed: number;
  billable: number;
  total: number;
}

interface ReportUserTimeEntry {
  id: string;
  email: string;
  totalHours: number;
}

export class HarvestService {
  private cache: Promise<HarvestProject[]> | undefined = undefined;

  constructor(public endpoint: string) {}

  fetchProjects(): Promise<HarvestProject[]> {
    if (!this.cache) {
      this.cache = fetchService
        .fetch(`${this.endpoint}/harvest/projects`, {})
        .then((r) => r.json())
        .catch((): any[] => []);
    }
    return this.cache;
  }

  fetchUsers(): Promise<HarvestUser[]> {
    return fetchService
      .fetch(`${this.endpoint}/harvest/users`, {})
      .then((r) => r.json());
  }

  private fetchProjectBudget(
    year: number,
    month: number
  ): Promise<ProjectBudgetEntry[]> {
    return fetchService
      .fetch(
        `${this.endpoint}/harvest/reports/budget/projects?year=${year}&month=${month}`
      )
      .then((r) => r.json())
      .catch((): any[] => []);
  }

  private fetchReportTeamTime(
    year: number,
    month: number
  ): Promise<ReportUserTimeEntry[]> {
    return fetchService
      .fetch(
        `${this.endpoint}/harvest/reports/time/teams?year=${year}&month=${month}`
      )
      .then((r) => r.json());
  }

  async fetchBudget(year: number, month: number): Promise<HarvestBudget> {
    const response = this.fetchProjectBudget(year, month).then((report) => {
      return report.reduce((budgets, r) => {
        const key = `${r.harvestProjectId}`;
        budgets.items[key] = {
          harvestProjectId: key,
          billable: r.billable,
          consumed: r.consumed || 0,
          total: r.total || 0,
        };
        return budgets;
      }, new HarvestBudget());
    });
    return response;
  }

  async fetchTeamHours(
    year: number,
    month: number
  ): Promise<HarvestTeamTimeReport> {
    const response = this.fetchReportTeamTime(year, month).then(
      (entries: ReportUserTimeEntry[]) => {
        return entries.reduce((all, entry) => {
          const email = (entry.email || "").toLowerCase();
          all[email] = {
            id: entry.id,
            email,
            hours: entry.totalHours,
          };
          return all;
        }, {} as HarvestTeamTimeReport);
      }
    );
    return response;
  }
}

const service = new HarvestService(env.endpoint);

export default service;
